import { rd, type RemoteData } from '@passionware/monads';
import {
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useState
} from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandLoading
} from 'v5/design-system/Command';
import {
  Input,
  type InputSize,
  type InputVariant
} from 'v5/design-system/Input';
import {
  Popover,
  PopoverAnchor,
  PopoverContent
} from 'v5/design-system/Popover';

/** What the list shows for one suggestion. */
export type SuggestOption = {
  id: string;
  label: string;
  /** A second, muted part ("Lazio, Italy"); `null` for none. */
  detail: string | null;
};

export type SuggestInputProps<T> = {
  /** The text in the input: whatever was typed, or the label of what was picked. */
  value: string;
  onChange(text: string): void;
  /** The search behind the list, by what was typed (a hook: called on every render). */
  useSuggestions(query: string): RemoteData<ReadonlyArray<T>>;
  toOption(item: T): SuggestOption;
  onPick(item: T): void;
  /** Shown in the list when nothing matches. */
  emptyContent: ReactNode;
  variant: InputVariant;
  size: InputSize;
  placeholder?: string;
  'aria-label'?: string;
  name?: string;
  inputRef?: Ref<HTMLInputElement>;
  onBlur?(event: FocusEvent<HTMLInputElement>): void;
};

/** Keys the suggestion list (cmdk) would take while it is closed. */
const LIST_KEYS = new Set(['Home', 'End']);

/**
 * A text input that suggests as you type: the text is the value (free text
 * is fine), and picking a suggestion replaces it. The list opens on typing
 * and drives from the input (↑ / ↓, Enter); Escape closes it without leaving
 * whatever the input sits in.
 */
export function SuggestInput<T>({
  value,
  onChange,
  useSuggestions,
  toOption,
  onPick,
  emptyContent,
  variant,
  size,
  placeholder,
  'aria-label': ariaLabel,
  name,
  inputRef,
  onBlur
}: SuggestInputProps<T>) {
  const [open, setOpen] = useState(false);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (open && event.key === 'Escape') {
      event.stopPropagation();
      setOpen(false);
      return;
    }
    if (!open && event.key === 'ArrowDown' && value.trim() !== '') {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (!open && LIST_KEYS.has(event.key)) event.stopPropagation();
  };

  return (
    // `contents`: the input stays where its host lays it out.
    <Command shouldFilter={false} loop className="contents">
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverAnchor asChild>
          <Input
            ref={inputRef}
            variant={variant}
            size={size}
            value={value}
            name={name}
            placeholder={placeholder}
            aria-label={ariaLabel}
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            autoComplete="off"
            onChange={event => {
              onChange(event.target.value);
              setOpen(event.target.value.trim() !== '');
            }}
            onKeyDown={onKeyDown}
            onBlur={event => {
              setOpen(false);
              onBlur?.(event);
            }}
          />
        </PopoverAnchor>
        <PopoverContent
          className="p-0"
          align="start"
          // Focus stays in the input: the list is driven from it.
          onOpenAutoFocus={event => event.preventDefault()}
          onMouseDown={event => event.preventDefault()}
          onInteractOutside={event => event.preventDefault()}
        >
          <SuggestList
            query={value}
            useSuggestions={useSuggestions}
            toOption={toOption}
            emptyContent={emptyContent}
            onPick={item => {
              onPick(item);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </Command>
  );
}

function SuggestList<T>({
  query,
  useSuggestions,
  toOption,
  emptyContent,
  onPick
}: Pick<
  SuggestInputProps<T>,
  'useSuggestions' | 'toOption' | 'emptyContent' | 'onPick'
> & { query: string }) {
  const suggestions = rd.useLastWithPlaceholder(useSuggestions(query));
  return (
    <CommandList>
      {rd
        .journey(suggestions)
        .wait(<CommandLoading />)
        .catch(() => (
          <CommandGroup>
            <CommandItem disabled className="text-fg-danger">
              Something went wrong
            </CommandItem>
          </CommandGroup>
        ))
        .map(items => (
          <>
            <CommandEmpty>{emptyContent}</CommandEmpty>
            <CommandGroup>
              {items.map(item => {
                const option = toOption(item);
                return (
                  <CommandItem
                    key={option.id}
                    value={option.id}
                    onSelect={() => onPick(item)}
                  >
                    <div className="space-x-2">
                      <span>{option.label}</span>
                      {option.detail !== null && (
                        <span className="text-sm text-fg-muted">
                          {option.detail}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        ))}
    </CommandList>
  );
}
